<script setup lang="ts">
import { Trash2, X } from 'lucide-vue-next'
import type { DeleteConfirmRequest } from '~/types/web-chat'

const props = defineProps<{ request: DeleteConfirmRequest }>()

const emit = defineEmits<{
  'confirm-delete': []
  'cancel-delete': []
}>()

const { t } = useI18n()

/**
 * The line shown once the card is settled. It reads `request.outcome` — what the delete actually
 * did — because `status === 'confirmed'` is set BEFORE the delete runs and says only that the user
 * said yes (MAR-957). No outcome yet means the delete is still in flight.
 */
const settledLine = computed(() => {
  const { status, outcome, items } = props.request
  if (status === 'cancelled') return { text: t('webChat.confirmCards.delete.cancelled'), failed: false }
  if (!outcome) return { text: t('webChat.confirmCards.delete.working'), failed: false }
  if (outcome.ok) return { text: t('webChat.confirmCards.delete.done', { n: outcome.succeeded ?? items.length }), failed: false }
  const done = outcome.succeeded ?? 0
  const failed = outcome.failed ?? items.length
  if (done > 0) {
    return { text: t('webChat.confirmCards.delete.partial', { done, total: done + failed, failed }), failed: true }
  }
  return { text: t('webChat.confirmCards.delete.failed', { n: failed }), failed: true }
})
</script>

<template>
  <div class="mt-3 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
    <div class="flex items-start gap-2">
      <div class="mt-0.5 h-5 w-5 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
        <Trash2 class="h-3 w-3 text-destructive" />
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-xs font-medium text-foreground">{{ t('webChat.confirmCards.delete.title') }}</p>
        <i18n-t keypath="webChat.confirmCards.delete.body" tag="p" class="mt-0.5 text-xs text-muted-foreground leading-relaxed" scope="global">
          <template #target>
            <span class="font-medium text-foreground">
              {{ request.items.length === 1 ? `"${request.items[0]?.name}"` : t('webChat.confirmCards.itemCount', { n: request.items.length }) }}
            </span>
          </template>
        </i18n-t>
        <div
          v-if="request.items.length > 1"
          class="mt-1 text-[11px] text-muted-foreground/80 leading-relaxed"
        >
          {{ request.items.slice(0, 3).map(item => item.name).join(', ') }}{{ request.items.length > 3 ? ` ${t('webChat.confirmCards.andMore', { n: request.items.length - 3 })}` : '' }}
        </div>
        <div
          v-if="request.status === 'pending'"
          class="mt-2 flex flex-wrap gap-1.5"
        >
          <Button
            type="button"
            variant="destructive"
            size="sm"
            class="gap-1.5"
            @click="emit('confirm-delete')"
          >
            <Trash2 class="h-3 w-3" />
            {{ t('webChat.confirmCards.delete.action') }}
          </Button>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent-lighter hover:text-foreground transition-colors"
            @click="emit('cancel-delete')"
          >
            <X class="h-3 w-3" />
            {{ t('webChat.confirmCards.cancel') }}
          </button>
        </div>
        <p
          v-else
          class="mt-2 text-[11px]"
          :class="settledLine.failed ? 'text-destructive' : 'text-muted-foreground'"
        >
          {{ settledLine.text }}
        </p>
      </div>
    </div>
  </div>
</template>
